import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAccount, useDisconnect } from 'wagmi';
import WalletConnect from '../../components/WalletConnect';
import PortfolioPreview from '../../components/discover/PortfolioPreview';
import CustomSwapWidget from '../../components/swap/CustomSwapWidget';
import { ZNSAddressDisplay } from '../../components/shared/ZNSAddressDisplay';

// ============================================
// ETH BRAND COLORS
// Oxford blue background with metallic blue borders
// ============================================
export const ETH_COLORS = {
  primary: '#396274', // Metallic blue
  secondary: '#60A5FA', // Light blue accent
  accent: '#04244c', // Oxford blue (darker)
  border: '#396274', // Metallic blue border
  borderHover: 'rgba(57, 98, 116, 0.8)', // Metallic blue border hover
  borderLight: 'rgba(57, 98, 116, 0.3)', // Metallic blue border subtle
  background: '#04244c', // Oxford blue background
  cardBackground: '#0a2d3d', // Slightly lighter blue for cards
  white: '#FFFFFF',
  lightBlue: '#60A5FA',
  darkBlue: '#1E40AF',
  // Gradients
  headerGradient: 'linear-gradient(135deg, #04244c 0%, #0a2d3d 100%)',
  cardGradient: 'linear-gradient(180deg, rgba(4, 36, 76, 0.8) 0%, rgba(10, 45, 61, 0.9) 100%)',
  buttonGradient: 'linear-gradient(135deg, #396274 0%, #4a7a8a 100%)',
  accentGradient: 'linear-gradient(135deg, #396274 0%, #60A5FA 100%)',
};

// ============ LAYOUT CONTAINER ============
const LayoutContainer = styled.div`
  min-height: 100vh;
  background: ${ETH_COLORS.background};
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

// ============ NAVBAR ============
const NavBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: transparent;
  border-bottom: 1px solid ${ETH_COLORS.border};
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
  
  @media (max-width: 768px) {
    height: 32px;
  }
`;

const LogoText = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${ETH_COLORS.white};
  letter-spacing: 0.5px;
  
  @media (max-width: 1024px) {
    font-size: 14px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const NavLink = styled.button<{ $active?: boolean }>`
  padding: 10px 16px;
  background: ${props => props.$active 
    ? 'rgba(57, 98, 116, 0.15)' 
    : 'transparent'};
  border: 1px solid ${props => props.$active 
    ? ETH_COLORS.border
    : 'transparent'};
  border-radius: 8px;
  color: ${props => props.$active ? ETH_COLORS.lightBlue : 'rgba(255, 255, 255, 0.7)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(57, 98, 116, 0.1);
    color: ${ETH_COLORS.lightBlue};
    border-color: ${ETH_COLORS.borderLight};
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChainBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(57, 98, 116, 0.1);
  border: 1px solid ${ETH_COLORS.border};
  border-radius: 20px;
  font-size: 13px;
  color: ${ETH_COLORS.lightBlue};
  
  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const ChainIcon = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
`;

const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  
  img {
    height: 16px;
    width: auto;
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const WalletBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(57, 98, 116, 0.1);
  border: 1px solid ${ETH_COLORS.border};
  border-radius: 10px;
  color: ${ETH_COLORS.lightBlue};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(57, 98, 116, 0.2);
    border-color: ${ETH_COLORS.borderHover};
  }
`;

// ============ CONTENT WRAPPER ============
const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  min-height: calc(100vh - 64px - 56px);
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

// ============ MAIN CONTENT ============
const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  padding: 24px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// ============ RIGHT SIDEBAR ============
const RightSidebar = styled.aside`
  width: 380px;
  min-width: 380px;
  border-left: 1px solid ${ETH_COLORS.border};
  background: transparent;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 64px - 56px);
  position: sticky;
  top: 64px;
  
  @media (max-width: 1200px) {
    width: 100%;
    min-width: 100%;
    border-left: none;
    border-top: 1px solid ${ETH_COLORS.border};
    max-height: none;
    position: relative;
    top: 0;
    flex-direction: row;
    flex-wrap: wrap;
    
    & > * {
      flex: 1;
      min-width: 300px;
    }
  }
  
  @media (max-width: 700px) {
    padding: 16px;
    flex-direction: column;
    
    & > * {
      min-width: 100%;
    }
  }
`;

const SidebarSection = styled.div`
  background: transparent;
  border: 1px solid ${ETH_COLORS.border};
  border-radius: 12px;
  padding: 16px;
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${ETH_COLORS.lightBlue};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ============ FOOTER ============
const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  background: transparent;
  border-top: 1px solid ${ETH_COLORS.border};
`;

const FooterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  
  a {
    color: ${ETH_COLORS.lightBlue};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
      color: ${ETH_COLORS.lightBlue};
    }
  }
`;

// ============ COMPONENT ============
const ETHLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isConnected, address } = useAccount();
  const { disconnect } = useDisconnect();
  
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <LayoutContainer>
      <NavBar>
        <NavLeft>
          <LogoContainer onClick={() => navigate('/app/eth/explore')}>
            <Logo src="/landing-assets/ETH_logo.png" alt="Ethereum" />
            <LogoText>Mavrk on ETH</LogoText>
          </LogoContainer>
          
          <NavLinks>
            <NavLink 
              $active={isActive('/app/eth/explore')}
              onClick={() => navigate('/app/eth/explore')}
            >
              Explore
            </NavLink>
            <NavLink 
              $active={isActive('/app/eth/trade')}
              onClick={() => navigate('/app/eth/trade')}
            >
              Trade
            </NavLink>
            <NavLink 
              $active={isActive('/app/eth/portfolio')}
              onClick={() => navigate('/app/eth/portfolio')}
            >
              Portfolio
            </NavLink>
          </NavLinks>
        </NavLeft>
        
        <NavRight>
          <ChainBadge>
            <ChainIcon src="/landing-assets/ETH_logo.png" alt="Ethereum" />
            Ethereum
          </ChainBadge>
          
          {isConnected && address ? (
            <WalletBadge onClick={() => disconnect()} title="Click to disconnect">
              <ZNSAddressDisplay address={address} truncateLength={4} showIcon={false} />
            </WalletBadge>
          ) : (
            <WalletConnect useENS={true} />
          )}
          
          <PoweredBy>
            Powered by
            <img src="/mavrk_logo_white.png" alt="Mavrk" />
          </PoweredBy>
        </NavRight>
      </NavBar>
      
      <ContentWrapper>
        <MainContent>
          <Outlet />
        </MainContent>
        
        <RightSidebar>
          {/* Portfolio Section */}
          <SidebarSection>
            <SidebarTitle>
              📊 Portfolio
            </SidebarTitle>
            <PortfolioPreview />
          </SidebarSection>
          
          {/* Swap Widget */}
          <SidebarSection>
            <SidebarTitle>
              💱 Swap
            </SidebarTitle>
            <CustomSwapWidget />
          </SidebarSection>
        </RightSidebar>
      </ContentWrapper>
      
      <Footer>
        <FooterContent>
          <span>Infrastructure by <a href="https://mavrk.xyz" target="_blank" rel="noopener noreferrer">Mavrk</a></span>
        </FooterContent>
      </Footer>
    </LayoutContainer>
  );
};

export default ETHLayout;

